(() => {
  // Match Shopify product percentage discounts: truncate the discount per unit
  // to whole cents before multiplying by quantity. Keep Liquid calculations in sync.
  const discountedUnitPrice = (price, discount) => price - Math.floor(price * discount / 100);

  const formatMoney = (cents, currency = 'USD') => {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency
    }).format(cents / 100);
  };

  class CircPdp {
    constructor(section) {
      this.section = section;
      this.variants = JSON.parse(section.querySelector('[data-product-json]')?.textContent || '[]');
      this.tierCopyConfig = section.querySelector('[data-tier-copy-config]')?.dataset || {};
      this.currency = this.tierCopyConfig.currency || 'USD';
      this.form = section.querySelector('[data-product-form]');
      this.stockRow = section.querySelector('[data-stock-availability]');
      this.stockVariants = new Map(JSON.parse(section.querySelector('[data-stock-availability-variants]')?.textContent || '[]').map((variant) => [Number(variant.id), variant]));
      this.track = section.querySelector('[data-gallery-track]');
      this.mediaItems = [...section.querySelectorAll('[data-media-id]')];
      this.dots = [...section.querySelectorAll('[data-gallery-dot]')];
      this.shippingCostNote = section.querySelector('[data-pdp-shipping-note]');
      this.buyButton = section.querySelector('[data-buy-button]');
      this.buyLabel = section.querySelector('[data-buy-label]');
      this.defaultBuyLabel = this.buyLabel?.textContent.trim() || 'Add to cart';
      this.tierInputs = [...section.querySelectorAll('[data-tier-quantity]')];
      this.modal = section.querySelector('[data-gallery-modal]');
      this.modalImage = section.querySelector('[data-gallery-modal-image]');
      this.modalThumbs = [...section.querySelectorAll('[data-gallery-modal-thumb]')];
      this.pointerStart = null;
      this.lastFocusedElement = null;
      this.initializeTier();
      const initialVariantId = Number(this.form?.querySelector('[data-variant-id]')?.value);
      const initialVariant = this.variants.find((variant) => Number(variant.id) === initialVariantId) || this.variants[0];
      if (initialVariant) {
        this.updateTierPrices(initialVariant.price);
        this.updateStockAvailability(initialVariant.id);
      }
      if (this.stockRow) {
        this.stockClockTimer = window.setInterval(() => this.updateStockAvailability(this.currentVariantId), 60_000);
        this.handleSectionUnload = (event) => {
          if (event.target === this.section || event.target?.contains?.(this.section)) {
            window.clearInterval(this.stockClockTimer);
            document.removeEventListener('shopify:section:unload', this.handleSectionUnload);
          }
        };
        document.addEventListener('shopify:section:unload', this.handleSectionUnload);
      }
      this.bindEvents();
    }

    initializeTier() {
      if (!this.tierInputs.length) return;
      const selected = this.tierInputs.find((input) => input.checked) || this.tierInputs[0];
      selected.checked = true;
      const quantity = this.section.querySelector('[data-quantity]');
      if (quantity) quantity.value = selected.value;
    }

    selectedTier() {
      return this.tierInputs.find((input) => input.checked);
    }

    selectedDiscountCode() {
      return this.selectedTier()?.dataset.discountCode?.trim() || '';
    }

    resolvedSuccessAction() {
      const action = this.section.dataset.successAction || 'theme';
      if (action !== 'theme') return action;
      return this.section.dataset.themeCartType === 'drawer' ? 'drawer' : 'cart';
    }

    bindEvents() {
      this.section.querySelectorAll('[data-options] input').forEach((input) => input.addEventListener('change', () => this.updateVariant()));
      this.tierInputs.forEach((input) => input.addEventListener('change', () => {
        const quantity = this.section.querySelector('[data-quantity]');
        if (quantity) quantity.value = input.value;
        this.updateShippingCostNote();
      }));
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.showMedia(Number(dot.dataset.galleryDot))));
      this.track?.addEventListener('scroll', () => this.updateDotsFromScroll(), { passive: true });
      this.form?.addEventListener('submit', (event) => this.submit(event));
      this.section.querySelector('[data-bundle-add]')?.addEventListener('click', (event) => this.addBundle(event));

      this.mediaItems.forEach((item, index) => {
        item.addEventListener('pointerdown', (event) => {
          this.pointerStart = { x: event.clientX, y: event.clientY, pointerId: event.pointerId };
        });
        item.addEventListener('pointerup', (event) => {
          if (!this.pointerStart || this.pointerStart.pointerId !== event.pointerId) return;
          const distance = Math.hypot(event.clientX - this.pointerStart.x, event.clientY - this.pointerStart.y);
          this.pointerStart = null;
          if (distance < 8) this.openGallery(index);
        });
        item.addEventListener('pointercancel', () => { this.pointerStart = null; });
        item.addEventListener('click', (event) => {
          event.preventDefault();
          if (event.detail === 0) this.openGallery(index);
        });
      });

      this.modalThumbs.forEach((thumb) => thumb.addEventListener('click', () => this.setModalImage(Number(thumb.dataset.galleryModalThumb))));
      this.section.querySelectorAll('[data-gallery-close]').forEach((button) => button.addEventListener('click', () => this.closeGallery()));
      document.addEventListener('keydown', (event) => {
        if (!this.modal || this.modal.hidden) return;
        if (event.key === 'Escape') this.closeGallery();
        if (event.key === 'ArrowRight') this.stepModal(1);
        if (event.key === 'ArrowLeft') this.stepModal(-1);
      });
    }

    selectedOptions() {
      return [...this.section.querySelectorAll('[data-options] fieldset')].map((fieldset) => fieldset.querySelector('input:checked')?.value);
    }

    updateVariant() {
      const options = this.selectedOptions();
      const variant = this.variants.find((item) => item.options.every((option, index) => option === options[index]));
      if (!variant) return;
      const variantInput = this.section.querySelector('[data-variant-id]');
      if (variantInput) variantInput.value = variant.id;
      const price = this.section.querySelector('[data-price]');
      if (price) price.textContent = formatMoney(variant.price, this.currency);
      const comparePrice = this.section.querySelector('[data-compare-price]');
      if (comparePrice) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          comparePrice.textContent = formatMoney(variant.compare_at_price, this.currency);
          comparePrice.classList.remove('is-hidden');
        } else comparePrice.classList.add('is-hidden');
      }
      if (this.buyButton) this.buyButton.disabled = !variant.available;
      if (this.buyLabel) this.buyLabel.textContent = variant.available ? this.defaultBuyLabel : 'Sold out';
      this.updateStockAvailability(variant.id);
      this.updateTierPrices(variant.price);
      if (variant.featured_media) {
        const index = this.mediaItems.findIndex((item) => Number(item.dataset.mediaId) === Number(variant.featured_media.id));
        if (index >= 0) this.showMedia(index);
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    updateStockAvailability(variantId) {
      if (!this.stockRow) return;
      this.currentVariantId = Number(variantId);
      const stock = this.stockVariants.get(this.currentVariantId);
      if (!stock?.in_stock) {
        this.stockRow.hidden = true;
        return;
      }

      const timeZone = this.stockRow.dataset.timeZone || 'Europe/Vienna';
      const cutoff = this.stockRow.dataset.cutoff || '14:00';
      const [cutoffHour, cutoffMinute] = cutoff.split(':').map(Number);
      const cutoffMinutes = (cutoffHour || 0) * 60 + (cutoffMinute || 0);
      const excludedDates = new Set((this.stockRow.dataset.excludedDates || '').split(/[\s,;]+/).map((date) => date.trim()).filter(Boolean));
      const now = new Date();
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone,
        year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short',
        hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
      }).formatToParts(now).reduce((result, part) => ({ ...result, [part.type]: part.value }), {});
      const today = `${parts.year}-${parts.month}-${parts.day}`;
      const todayUtc = new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), 12));
      const weekday = todayUtc.getUTCDay();
      const minutesNow = Number(parts.hour) * 60 + Number(parts.minute);
      const isSaturdayAllowed = this.stockRow.dataset.shipSaturday === 'true';
      const isDispatchWeekday = (day) => (day >= 1 && day <= 5) || (day === 6 && isSaturdayAllowed);
      const canDispatchToday = isDispatchWeekday(weekday) && !excludedDates.has(today);
      const beforeCutoff = canDispatchToday && minutesNow < cutoffMinutes;

      let nextDispatchDate = null;
      for (let offset = 1; offset <= 366; offset += 1) {
        const candidate = new Date(todayUtc.getTime() + offset * 86_400_000);
        const candidateKey = `${candidate.getUTCFullYear()}-${String(candidate.getUTCMonth() + 1).padStart(2, '0')}-${String(candidate.getUTCDate()).padStart(2, '0')}`;
        if (isDispatchWeekday(candidate.getUTCDay()) && !excludedDates.has(candidateKey)) {
          nextDispatchDate = candidate;
          break;
        }
      }
      const dispatchDate = beforeCutoff ? todayUtc : nextDispatchDate;
      if (!dispatchDate) {
        this.stockRow.hidden = true;
        return;
      }

      const locale = document.documentElement.lang || 'de-AT';
      const dispatchDay = dispatchDate
        ? new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' }).format(dispatchDate)
        : '';
      const nextDay = new Date(todayUtc.getTime() + 86_400_000);
      const dispatchIsTomorrow = dispatchDate && dispatchDate.getUTCFullYear() === nextDay.getUTCFullYear()
        && dispatchDate.getUTCMonth() === nextDay.getUTCMonth() && dispatchDate.getUTCDate() === nextDay.getUTCDate();

      const label = this.stockRow.dataset.label || '';
      let ready = '';
      if (beforeCutoff) ready = this.stockRow.dataset.readyToday || '';
      else if (dispatchIsTomorrow) ready = this.stockRow.dataset.readyTomorrow || '';
      else ready = (this.stockRow.dataset.readyNext || '').replaceAll('{day}', dispatchDay);
      const delivery = (this.stockRow.dataset.deliveryCopy || '')
        .replaceAll('{cutoff}', cutoff)
        .replaceAll('{day}', dispatchDay);

      const labelNode = this.stockRow.querySelector('[data-stock-label]');
      const readyNode = this.stockRow.querySelector('[data-stock-ready]');
      const mainNode = this.stockRow.querySelector('[data-stock-main]');
      const separator = this.stockRow.querySelector('[data-stock-separator]');
      const deliveryNode = this.stockRow.querySelector('[data-stock-delivery]');
      if (labelNode && labelNode.textContent !== label) labelNode.textContent = label;
      if (readyNode && readyNode.textContent !== ready) readyNode.textContent = ready;
      if (mainNode) mainNode.hidden = !label && !ready;
      if (separator) separator.hidden = !label || !ready;
      if (deliveryNode) {
        if (deliveryNode.textContent !== delivery) deliveryNode.textContent = delivery;
        deliveryNode.hidden = !delivery;
      }
      this.stockRow.hidden = !label && !ready && !delivery;
    }

    showMedia(index) {
      const media = this.mediaItems[index];
      if (!media) return;
      this.mediaItems.forEach((item) => item.classList.remove('is-active'));
      this.dots.forEach((dot) => dot.classList.remove('is-active'));
      media.classList.add('is-active');
      this.dots.filter((dot) => Number(dot.dataset.galleryDot) === index).forEach((dot) => dot.classList.add('is-active'));
      if (window.matchMedia('(max-width: 749px)').matches) {
        media.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }

    updateTierPrices(variantPrice) {
      this.shippingVariantPrice = Number(variantPrice);
      this.tierInputs.forEach((input) => {
        const tier = input.closest('.circ-pdp__tier');
        if (!tier) return;
        if (input.dataset.manualPrice === 'true') return;
        const quantity = Number(input.value) || 1;
        const discount = Number(input.dataset.discountPercent || 0);
        const unit = discountedUnitPrice(variantPrice, discount);
        const total = unit * quantity;
        const unitPrice = tier.querySelector('[data-tier-unit-price]');
        const netWeightGrams = Number(this.tierCopyConfig.netWeightG || 0);
        if (unitPrice && netWeightGrams > 0) {
          unitPrice.textContent = `(${formatMoney(total * 1000 / (netWeightGrams * quantity), this.currency)}/kg, ${this.tierCopyConfig.taxLabel || ''})`;
        }
        const savings = variantPrice * quantity - total;
        const price = tier.querySelector('.circ-pdp__tier-price');
        const totalText = tier.querySelector('.circ-pdp__tier-total');
        const compare = tier.querySelector('s');
        if (tier.classList.contains('circ-pdp__tier--per_item')) {
          const priceAmount = tier.querySelector('.circ-pdp__tier-price-amount');
          if (priceAmount) priceAmount.textContent = formatMoney(unit, this.currency);
          const unitLabel = tier.querySelector('.circ-pdp__tier-unit');
          if (unitLabel) unitLabel.textContent = this.tierCopyConfig.perItemLabel || '';
          if (totalText) totalText.textContent = `${this.tierCopyConfig.totalLabel || ''} ${formatMoney(total, this.currency)}`.trim();
        } else {
          if (price) price.textContent = formatMoney(total, this.currency);
          if (totalText) totalText.textContent = `${formatMoney(unit, this.currency)} ${this.tierCopyConfig.perItemLabel || ''}`.trim();
        }
        if (compare && discount > 0 && input.dataset.manualCompare !== 'true') {
          const compareValue = tier.classList.contains('circ-pdp__tier--per_item') ? variantPrice : variantPrice * quantity;
          compare.textContent = formatMoney(compareValue, this.currency);
        }
        const savingsText = tier.querySelector('.circ-pdp__tier-savings');
        if (savingsText && discount > 0 && !savingsText.dataset.savingsOverride) {
          const roundedSavings = this.roundSavings(savings);
          const template = this.tierCopyConfig.template || 'Save {saving_percent}%';
          savingsText.textContent = template
            .replaceAll('{quantity}', String(quantity))
            .replaceAll('{saving_percent}', String(discount))
            .replaceAll('{saving_amount}', formatMoney(roundedSavings, this.currency))
            .replaceAll('{total}', formatMoney(total, this.currency))
            .replaceAll('{per_item}', formatMoney(unit, this.currency));
        }
        let estimateCue = tier.querySelector('.circ-pdp__tier-estimate');
        if (discount > 0 && this.tierCopyConfig.estimateCue) {
          if (!estimateCue) {
            estimateCue = document.createElement('small');
            estimateCue.className = 'circ-pdp__tier-estimate';
            tier.append(estimateCue);
          }
          estimateCue.textContent = this.tierCopyConfig.estimateCue;
        } else estimateCue?.remove();
      });
      this.updateShippingCostNote();
    }

    updateShippingCostNote() {
      const note = this.shippingCostNote;
      if (!note) return;
      const tier = this.selectedTier();
      const quantity = Number(tier?.value || 1);
      const discount = Number(tier?.dataset.discountPercent || 0);
      const threshold = Number(note.dataset.threshold);
      // Unknown/manual prices keep the neutral rate-and-threshold wording.
      const canEstimate = tier?.dataset.manualPrice !== 'true'
        && Number.isFinite(this.shippingVariantPrice) && this.shippingVariantPrice >= 0
        && Number.isFinite(quantity) && quantity > 0
        && Number.isFinite(discount) && discount >= 0 && discount <= 100
        && threshold > 0;
      const total = discountedUnitPrice(this.shippingVariantPrice, discount) * quantity;
      const qualifies = canEstimate && total >= threshold;
      note.textContent = qualifies ? note.dataset.freeCopy : note.dataset.paidCopy;
      note.classList.toggle('is-free', qualifies);
    }

    roundSavings(cents) {
      const mode = this.tierCopyConfig.rounding || 'exact';
      if (mode === 'nearest') return Math.round(cents / 100) * 100;
      if (mode === 'up') return Math.ceil(cents / 100) * 100;
      if (mode === 'down') return Math.floor(cents / 100) * 100;
      return cents;
    }

    updateDotsFromScroll() {
      if (!window.matchMedia('(max-width: 749px)').matches || !this.track || !this.mediaItems.length) return;
      const center = this.track.scrollLeft + this.track.clientWidth / 2;
      let closest = 0;
      this.mediaItems.forEach((item, index) => {
        const itemDistance = Math.abs(item.offsetLeft + item.clientWidth / 2 - center);
        const closestDistance = Math.abs(this.mediaItems[closest].offsetLeft + this.mediaItems[closest].clientWidth / 2 - center);
        if (itemDistance < closestDistance) closest = index;
      });
      this.dots.forEach((dot) => dot.classList.toggle('is-active', Number(dot.dataset.galleryDot) === closest));
      this.mediaItems.forEach((item, index) => item.classList.toggle('is-active', index === closest));
    }

    openGallery(index) {
      if (!this.modal || !this.modalImage) return;
      this.lastFocusedElement = document.activeElement;
      this.setModalImage(index);
      this.modal.hidden = false;
      document.documentElement.classList.add('circ-pdp-gallery-open');
      this.modal.querySelector('[data-gallery-close]')?.focus();
    }

    closeGallery() {
      if (!this.modal) return;
      this.modal.hidden = true;
      document.documentElement.classList.remove('circ-pdp-gallery-open');
      this.lastFocusedElement?.focus?.();
    }

    setModalImage(index) {
      const thumb = this.modalThumbs[index];
      const media = this.mediaItems[index];
      if (!this.modalImage || (!thumb && !media)) return;
      this.modalImage.src = thumb?.dataset.galleryModalSrc || media?.dataset.fullImage || '';
      this.modalImage.alt = thumb?.dataset.galleryModalAlt || media?.querySelector('img')?.alt || '';
      this.modalThumbs.forEach((item, itemIndex) => item.classList.toggle('is-active', itemIndex === index));
      this.modalIndex = index;
      thumb?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }

    stepModal(direction) {
      if (!this.modalThumbs.length) return;
      const next = (Number(this.modalIndex || 0) + direction + this.modalThumbs.length) % this.modalThumbs.length;
      this.setModalImage(next);
    }

    redirectWithOptionalDiscount(destination) {
      const root = window.Shopify.routes.root;
      const discountCode = this.selectedDiscountCode();
      if (!discountCode) {
        window.location.href = `${root}${destination}`;
        return;
      }
      window.location.href = `${root}discount/${encodeURIComponent(discountCode)}?redirect=/${destination}`;
    }

    showAddError(error) {
      const message = this.section.querySelector('[data-add-error]');
      if (!message) return;
      let customerMessage = 'Der Artikel konnte nicht zum Warenkorb hinzugefügt werden. Bitte versuche es erneut.';
      try {
        const payload = JSON.parse(error?.message || '');
        customerMessage = payload.description || payload.message || customerMessage;
      } catch (_) {
        if (error?.message && !error.message.trim().startsWith('<')) customerMessage = error.message;
      }
      message.textContent = customerMessage;
      message.hidden = false;
    }

    clearAddError() {
      const message = this.section.querySelector('[data-add-error]');
      if (message) {
        message.textContent = '';
        message.hidden = true;
      }
    }

    async submit(event) {
      event.preventDefault();
      if (!this.buyButton || !this.form) return;
      this.buyButton.disabled = true;
      this.clearAddError();
      let added = false;
      try {
        const addResult = await this.addItems(new FormData(this.form));
        added = true;
        const action = this.resolvedSuccessAction();
        if (action === 'checkout') this.redirectWithOptionalDiscount('checkout');
        else if (action === 'cart') this.redirectWithOptionalDiscount('cart');
        else if (action === 'drawer') {
          await this.finishDrawerAdd(addResult);
          this.buyButton.disabled = false;
        }
        else {
          this.buyButton.disabled = false;
          document.dispatchEvent(new CustomEvent('circ-pdp:added', { bubbles: true, detail: { discountCode: this.selectedDiscountCode() } }));
        }
      } catch (error) {
        console.error('Circ PDP add to cart failed:', error);
        if (added && this.resolvedSuccessAction() === 'drawer') {
          this.showAddError(new Error('Der Artikel wurde hinzugefügt, aber der Warenkorb konnte nicht aktualisiert werden. Bitte öffne ihn über das Warenkorbsymbol.'));
          this.buyButton.disabled = false;
          return;
        }
        this.showAddError(error);
        this.buyButton.disabled = false;
      }
    }

    async addBundle(event) {
      const button = event.currentTarget;
      button.disabled = true;
      const formData = new FormData();
      formData.append('id', button.dataset.variantId);
      formData.append('quantity', '1');
      this.clearAddError();
      let added = false;
      try {
        const addResult = await this.addItems(formData);
        added = true;
        const action = this.resolvedSuccessAction();
        if (action === 'drawer') await this.finishDrawerAdd(addResult);
        else if (action === 'cart' || action === 'checkout') this.redirectWithOptionalDiscount(action);
        else document.dispatchEvent(new CustomEvent('circ-pdp:added', { bubbles: true }));
      } catch (error) {
        console.error('Circ PDP bundle add failed:', error);
        if (added && this.resolvedSuccessAction() === 'drawer') {
          this.showAddError(new Error('Der Artikel wurde hinzugefügt, aber der Warenkorb konnte nicht aktualisiert werden. Bitte öffne ihn über das Warenkorbsymbol.'));
          return;
        }
        this.showAddError(error);
      } finally {
        button.disabled = false;
      }
    }

    async finishDrawerAdd(addResult) {
      let drawerResult = addResult;
      const discountCode = this.selectedDiscountCode();
      let discountFailure = '';
      if (discountCode) {
        try {
          drawerResult = await this.applyTierDiscount(discountCode);
          if (drawerResult.discountError) {
            discountFailure = 'Der Rabattcode konnte nicht angewendet werden. Der Artikel wurde trotzdem hinzugefügt.';
          }
        } catch (error) {
          console.error('Circ PDP discount code failed:', error);
          discountFailure = 'Der Rabattcode konnte nicht angewendet werden. Der Artikel wurde trotzdem hinzugefügt.';
        }
      }
      await this.openCartDrawer(drawerResult);
      if (discountFailure) this.showCartDiscountError(discountFailure);
    }

    async applyTierDiscount(discountCode) {
      const cartItems = document.querySelector('cart-items-component');
      const sectionId = cartItems?.dataset.sectionId;

      const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      if (!cartResponse.ok) throw new Error('Could not read the current cart discounts.');
      const cart = await cartResponse.json();
      const cartCodes = [
        ...(cart.discount_codes || []).map((entry) => entry.code),
        ...(cart.cart_level_discount_applications || [])
          .filter((application) => application.type === 'discount_code' && application.title)
          .map((application) => application.title),
        ...(cart.items || []).flatMap((item) => (item.line_level_discount_allocations || [])
          .map((allocation) => allocation.discount_application)
          .filter((application) => application?.type === 'discount_code' && application.title)
          .map((application) => application.title))
      ].map((code) => code?.trim()).filter(Boolean);
      const visibleCodes = [...document.querySelectorAll('.cart-discount__pill[data-discount-code]')]
        .map((pill) => pill.dataset.discountCode?.trim())
        .filter(Boolean);
      const discountCodes = [...new Set([...cartCodes, ...visibleCodes, discountCode])];
      const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          discount: discountCodes.join(','),
          ...(sectionId ? { sections: [sectionId], sections_url: window.location.pathname } : {})
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'Could not apply discount code.');
      const appliedCode = result.discount_codes?.find((entry) => entry.code?.toLowerCase() === discountCode.toLowerCase());
      const appliedApplication = result.cart_level_discount_applications?.find((entry) => entry.type === 'discount_code' && entry.title?.toLowerCase() === discountCode.toLowerCase());
      result.discountError = Array.isArray(result.discount_codes)
        ? !appliedCode || appliedCode.applicable === false
        : !appliedApplication;
      return result;
    }

    showCartDiscountError(message) {
      const drawer = document.querySelector('theme-drawer#cart-drawer');
      const error = drawer?.querySelector('.cart-discount__error');
      const codeError = drawer?.querySelector('.cart-discount__error-text');
      if (error && codeError) {
        codeError.textContent = message;
        error.classList.remove('hidden');
        codeError.classList.remove('hidden');
      } else this.showAddError(new Error(message));
    }

    async addItems(body) {
      const cartItems = document.querySelector('cart-items-component');
      const sectionId = cartItems?.dataset.sectionId;
      if (body instanceof FormData && sectionId) {
        if (!body.has('sections')) body.append('sections', sectionId);
        if (!body.has('sections_url')) body.append('sections_url', window.location.pathname);
      }
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body
      });
      if (!response.ok) throw new Error(await response.text());
      return response.json();
    }

    async openCartDrawer(addResult = {}) {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      if (!response.ok) throw new Error('Could not refresh cart');
      const cart = await response.json();
      const updateEvent = new CustomEvent('shopify:cart:lines-update', {
        bubbles: true,
        detail: { itemCount: cart?.item_count || 0, sections: addResult.sections }
      });
      updateEvent.action = 'add';
      updateEvent.context = 'product';
      updateEvent.promise = Promise.resolve({ cart: { totalQuantity: cart?.item_count || 0 }, detail: updateEvent.detail });
      document.dispatchEvent(updateEvent);
      const cartItems = document.querySelector('cart-items-component');
      await cartItems?.fetchCartData?.();
      const drawer = document.querySelector('theme-drawer#cart-drawer');
      if (drawer?.open) drawer.open();
      else window.location.href = `${window.Shopify.routes.root}cart`;
    }
  }

  const init = (root = document) => root.querySelectorAll('[data-circ-pdp]').forEach((section) => {
    if (!section.dataset.initialized) {
      section.dataset.initialized = 'true';
      new CircPdp(section);
    }
  });

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();

